import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { UploadService } from '../../services/upload.service';
import { FormControl, Validators } from '@angular/forms';
import { imageType } from '../../validators/image-type-validator';

@Component({
  selector: 'app-upload',
  templateUrl: './upload.component.html',
  styleUrls: ['./upload.component.scss'],
})
export class UploadComponent implements OnInit, OnChanges {
  @Input() label: string = '';

  @Input() required: boolean = false;

  @Input() placeholder: string = '';

  /**
   * v2 forms render their own dropzone and hide this widget's native Material
   * field. Set this so the component owns the validation label too (a single,
   * consistently-styled `.field__error` shown below the dropzone) instead of
   * every form duplicating its own error markup.
   */
  @Input() showV2Error: boolean = false;

  @ViewChild('fileInput') fileInput: ElementRef;

  public _upload = inject(UploadService);

  public uploadControl = new FormControl(null);

  public uploadedFile: any;

  public allowedImageTypes: string[] = ['image/jpeg', 'image/jpg', 'image/png'];

  public showIncorrectImageTypeError: boolean = false;

  @Output() onUpload = new EventEmitter();

  ngOnInit(): void {
    // Intentionally no-op; validators are set in ngOnChanges to avoid ExpressionChangedAfterItHasBeenCheckedError
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['required']) {
      this.applyValidators();
    }
  }

  private applyValidators() {
    if (this.required) {
      this.uploadControl.setValidators([Validators.required, imageType]);
    } else {
      this.uploadControl.setValidators(imageType);
    }
    // Do not emit events to avoid CD re-evaluation chains
    this.uploadControl.updateValueAndValidity({ emitEvent: false });
  }

  /** Select image from computer
   * @param event Select event
   */
  public selectImage(event: any): void {
    if (event.target.files[0]) {
      this.showIncorrectImageTypeError = false;
      this.uploadControl.setValue(event.target.files[0].name);
      this.uploadedFile = event.target.files[0];
      this._upload.setFile(event.target.files[0]);
      this.checkUploadControl(event.target.files[0]);
      this.fileInput.nativeElement.blur();
    }
  }

  private checkUploadControl(imageFile: any) {
    this.uploadControl.markAsDirty();
    this.uploadControl.markAsPristine();
    this.uploadControl.updateValueAndValidity();
    if (this.uploadControl.invalid) return;
    this.onUpload.emit(imageFile);
  }

  /** Reset native element */
  public resetInput() {
    this.fileInput.nativeElement.value = null;
    this.uploadControl.setValue('');
    // Clear the cached file too, otherwise a deleted image would still be
    // uploaded on the next save via the stale uploadedFile reference.
    this.uploadedFile = undefined;
    this._upload.resetData();
  }

  /**
   * Translation key for the current validation error, or null when valid /
   * untouched. Consumed by the v2 error label (see {@link showV2Error}).
   */
  public get errorKey(): string | null {
    const c = this.uploadControl;
    if (!c || !c.touched) return null;
    if (c.hasError('required')) return 'errors.required';
    if (c.hasError('imageType')) return 'errors.incorrectImageType';
    return null;
  }

  /** Check control if required */
  public checkControl(): boolean {
    if (!this.required) return true;

    this.uploadControl.markAllAsTouched();
    this.uploadControl.updateValueAndValidity();
    if (!this.uploadControl.valid) return false;

    return true;
  }
}
